import React, { useEffect, useRef, useState } from 'react';
import { Camera, CameraOff, ChevronDown, QrCode, Search, X } from 'lucide-react';
import { cn } from '../utils/cn';
import { ilmsInputClass } from './ILMSForm';
import { animalsApi } from '../services/resources';
import type { RefOption } from '../features/common/referenceData';

/**
 * Animal picker used in place of a plain <select> wherever a form needs to
 * choose one animal (Milk Sessions, Health Records, Breeding, ...) — with
 * hundreds of animals a scrolling dropdown is painful, so this adds
 * type-to-search plus a camera QR scan that resolves straight to the animal
 * (same lookup as the standalone Scan QR page).
 */
export function ILMSAnimalPicker({
  id,
  value,
  onChange,
  options,
  error,
  placeholder = 'Search by name or ID, or scan QR…',
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: RefOption[];
  error?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [extraOptions, setExtraOptions] = useState<RefOption[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<import('html5-qrcode').Html5Qrcode | null>(null);
  const scannerElId = `${id ?? 'animal-picker'}-qr`;

  const allOptions = [...extraOptions, ...options];
  const selected = allOptions.find((o) => o.value === value);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = query.trim()
    ? allOptions.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : allOptions;

  const select = (opt: RefOption) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {
        // already stopped
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setScanError(null);
    setOpen(true);
    setScanning(true); // mounts/reveals the target <div> before we touch html5-qrcode

    const config = { fps: 10, qrbox: { width: 220, height: 220 } };
    const onDecoded = async (decodedText: string) => {
      await stopScanner();
      try {
        const animal = await animalsApi.byQr(decodedText);
        setExtraOptions((prev) => (prev.some((o) => o.value === animal.id) ? prev : [{ value: animal.id, label: `${animal.animal_id} — ${animal.animal_name ?? 'Animal'}` }, ...prev]));
        onChange(animal.id);
        setOpen(false);
      } catch {
        setScanError(`No animal found for this QR code. Try searching by name/ID instead.`);
      }
    };
    const onFrameFailure = () => {};

    // Everything below — including the constructor, which throws
    // synchronously if its target element isn't in the DOM yet — is now
    // inside this try/catch, so nothing can fail as a silent, invisible,
    // unhandled promise rejection again.
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const instance = new Html5Qrcode(scannerElId);
      scannerRef.current = instance;
      try {
        await instance.start({ facingMode: 'environment' }, config, onDecoded, onFrameFailure);
      } catch (firstErr) {
        await instance.start({ facingMode: 'user' }, config, onDecoded, onFrameFailure).catch(() => {
          throw firstErr;
        });
      }
    } catch (err) {
      setScanning(false);
      setScanError(
        `Unable to access the camera${err instanceof Error && err.message ? ` (${err.message})` : ''}. Allow camera permission, or search by name/ID instead.`
      );
    }
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex gap-1.5">
        <button
          type="button"
          id={id}
          onClick={() => setOpen((v) => !v)}
          className={cn(ilmsInputClass(error), 'flex items-center justify-between text-left', !selected && 'text-ink-400')}
        >
          <span className="truncate">{selected ? selected.label : 'Select animal…'}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-400" />
        </button>
        <button
          type="button"
          onClick={() => (scanning ? stopScanner() : startScanner())}
          title="Scan QR"
          aria-label="Scan animal QR tag"
          className={cn(
            'h-11 w-11 shrink-0 flex items-center justify-center rounded-md border',
            scanning ? 'bg-critical-50 border-critical-200 text-critical-700' : 'bg-brand-gold-50 border-brand-gold-200 text-brand-gold-700 hover:bg-brand-gold-100'
          )}
        >
          {scanning ? <CameraOff className="h-4 w-4" /> : <QrCode className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-ink-300/30 bg-white shadow-popover overflow-hidden">
          {/* Always mounted while open (only visually toggled via `hidden`):
              html5-qrcode needs its target <div id={scannerElId}> to already
              exist in the DOM the moment `new Html5Qrcode(...)` runs. Gating
              this behind `{scanning && ...}` meant the div didn't exist yet
              at construction time (scanning flips true afterwards), so the
              library had nowhere to attach video — no error, just nothing. */}
          <div className="p-3 border-b border-ink-300/10 flex flex-col items-center gap-2" hidden={!scanning}>
            <div id={scannerElId} className="w-full max-w-[220px] min-h-[220px] rounded-md overflow-hidden bg-black" />
            <p className="text-helper-text text-ink-400 flex items-center gap-1">
              <Camera className="h-3 w-3" /> Point the camera at the animal's QR tag
            </p>
          </div>
          {scanError && <p className="text-error-text text-critical-600 px-3 pt-2">{scanError}</p>}
          {!scanning && (
            <div className="p-2 border-b border-ink-300/10 flex items-center gap-2">
              <Search className="h-4 w-4 text-ink-400 shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={placeholder}
                className="flex-1 min-w-0 text-body outline-none"
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
                  <X className="h-4 w-4 text-ink-400" />
                </button>
              )}
            </div>
          )}
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-secondary text-ink-400 px-3 py-3">No matching animals.</p>
            ) : (
              filtered.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => select(opt)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 text-body hover:bg-brand-green-50',
                    opt.value === value && 'bg-brand-green-50 font-medium text-brand-green-800'
                  )}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
